// HU-45 · Mantener las plantillas de carta por autoridad. F-07, Sprint 2 (alcance mínimo:
// tres plantillas sembradas por TT-1 —SENASA, SAG, SENASAG— y el mantenimiento básico).
//
// LA FRASE QUE DECIDE EL DISEÑO (RN-24, C4): "una carta ya emitida conserva el texto de la
// versión con la que se generó". Por eso NO hay «editar»: publicar es crear la versión
// siguiente, y la anterior queda en el historial, consultable y descargable.
//
// LOS HUECOS SE ESCRIBEN {{campo}} (RN-37, C9). La vista previa (C2, C5) es la MISMA
// combinación que usará la emisión de la carta: un campo sin dato sale
// «[PENDIENTE: campo]» y la carta no se puede firmar hasta resolverlo.
//
// EL ARCHIVO DE WORD (C7, C8): se sube en base64 dentro de un JSON —un archivo por versión
// no justifica multipart— y se descarga tal cual se subió. Una versión publicada como
// texto (la semilla TT-1) se descarga como un .docx generado desde el texto.
//
// POR AUTORIDAD (CC-02, bloque B3; RN-75 y RN-81). Cada función acepta el código de una
// autoridad del país y lo manda como `?autoridad=`. Sin él, el servidor trabaja sobre la
// serie de la PREDETERMINADA: las rutas por país son su alias, así que con una sola autoridad
// las peticiones son exactamente las de antes. `plantillasPorAutoridad` dice, por autoridad,
// si tiene plantilla propia, si usa la de la predeterminada o si no tiene ninguna.

import { pedir, pedirBinario, segPais } from './cliente';

export type Plantilla = {
  plantilla_id: string;
  pais: string;
  version: number;
  texto: string;
  vigente_desde: string;
  vigente_hasta: string | null;
  creado_por: string | null;
  archivo_nombre: string | null;
  campos_combinables: string[];
  referencia: string;
  /** BUG-1101: en el día del país, la que rige hoy, la que empieza después o la que ya terminó. */
  estado_de_la_vigencia: EstadoDeLaVigencia;
  /** «desde 09/10/2026 · vigente», «programada desde 16/10/2026», «desde … hasta … · anterior». */
  texto_de_la_vigencia: string;
  /** CC-02: la autoridad dueña de la serie (null solo en un país sin autoridades registradas). */
  autoridad_codigo?: string | null;
};

/** BUG-1101: el estado de una versión lo decide el servidor, no la ausencia de fecha de fin. */
export type EstadoDeLaVigencia = 'vigente' | 'programada' | 'anterior';

/** CC-02 (B3): una autoridad del país y la plantilla que hoy la rige, derivada por el servidor. */
export type PlantillaDeLaAutoridad = {
  pais: string;
  codigo: string;
  nombre: string;
  es_predeterminada: boolean;
  activa: boolean;
  por_confirmar: boolean;
  version_vigente: number | null;
  /** `con_plantilla` · `prestada` (usa la de la predeterminada, RN-81) · `sin_plantilla`. */
  estado_de_plantilla: string;
  /** «SANIPES · v1», «Sin plantilla: se usa la de SENASA» o «Sin plantilla». */
  texto_de_plantilla: string;
  prestada_de: string | null;
  prestada_de_nombre: string | null;
  version_prestada: number | null;
};

export type CartaCombinada = {
  referencia: string;
  texto: string;
  campos: string[];
  pendientes: string[];
  lista_para_firmar: boolean;
};

const RUTA = '/administracion/plantillas-de-carta';

/** `?autoridad=SANIPES` (o `&` si ya hay consulta); sin autoridad, nada: la predeterminada. */
function conAutoridad(autoridad: string | undefined, separador: '?' | '&' = '?'): string {
  return autoridad ? `${separador}autoridad=${encodeURIComponent(autoridad)}` : '';
}

/** La vigente hoy (o el día indicado). Sin plantilla responde 422 con la frase de C3. */
export function verPlantilla(pais: string, dia?: string, autoridad?: string) {
  const consulta = dia ? `?dia=${encodeURIComponent(dia)}` : '';
  return pedir<Plantilla>(`${RUTA}/${segPais(pais)}${consulta}${conAutoridad(autoridad, consulta ? '&' : '?')}`);
}

export function versionesDePlantilla(pais: string, autoridad?: string) {
  return pedir<Plantilla[]>(`${RUTA}/${segPais(pais)}/versiones${conAutoridad(autoridad)}`);
}

/** CC-02 (B3): las autoridades del país, la predeterminada primero, con su plantilla de hoy. */
export function plantillasPorAutoridad(pais: string) {
  return pedir<PlantillaDeLaAutoridad[]>(`${RUTA}/${segPais(pais)}/autoridades`);
}

/** C7/C8: publica la versión siguiente desde un .docx. `contenidoBase64` sin prefijo data:. */
export function publicarDesdeArchivo(
  pais: string, nombreArchivo: string, contenidoBase64: string, desde?: string, autoridad?: string,
) {
  return pedir<Plantilla>(`${RUTA}/${segPais(pais)}/archivo${conAutoridad(autoridad)}`, {
    metodo: 'POST',
    cuerpo: { nombre_archivo: nombreArchivo, contenido_base64: contenidoBase64, desde: desde || null },
  });
}

/** Publica la versión siguiente desde texto (el editor de la pantalla). */
export function publicarTexto(pais: string, texto: string, desde?: string, autoridad?: string) {
  return pedir<Plantilla>(`${RUTA}/${segPais(pais)}${conAutoridad(autoridad)}`, {
    metodo: 'POST',
    cuerpo: { texto, desde: desde || null },
  });
}

/** C8: el .docx de una versión, vigente o no. */
export function descargarVersion(pais: string, version: number, autoridad?: string) {
  return pedirBinario(`${RUTA}/${segPais(pais)}/versiones/${Number(version)}/archivo${conAutoridad(autoridad)}`);
}

/** C2/C5/C9: la carta con los datos puestos en cada hueco. */
export function vistaPrevia(pais: string, datos: Record<string, string>, version?: number, autoridad?: string) {
  return pedir<CartaCombinada>(`${RUTA}/${segPais(pais)}/vista-previa${conAutoridad(autoridad)}`, {
    metodo: 'POST',
    cuerpo: { datos, version: version ?? null },
  });
}

/** Lee un File del navegador como base64 puro (sin el prefijo `data:...;base64,`). */
export function archivoABase64(archivo: Blob): Promise<string> {
  return new Promise((resolver, rechazar) => {
    const lector = new FileReader();
    lector.onerror = () => rechazar(new Error('No se pudo leer el archivo.'));
    lector.onload = () => {
      const resultado = String(lector.result ?? '');
      resolver(resultado.slice(resultado.indexOf(',') + 1));
    };
    lector.readAsDataURL(archivo);
  });
}

/** HU-45b-C1 (endpoint 54): los campos que el sistema sabe rellenar, con su etiqueta. */
export type CampoCombinable = { clave: string; etiqueta: string };

export function camposCombinables() {
  return pedir<{ campos: CampoCombinable[] }>(`${RUTA}/campos`);
}
